import { useCallback, useEffect, useRef, type FocusEvent } from 'react'
import { ChatPane } from '@/components/layout/chat-pane'
import type { ChatPaneProps } from '@/components/layout/chat-pane.types'
import {
  shouldRestoreAgentPanelOnComposerFocus,
  useChatSurfaceRegistry,
} from '@/hooks/use-chat-surface-registry'
import { useHostedChatThread } from '@/hooks/use-hosted-chat-thread'
import { useWorkspace } from '@/providers/workspace-provider'

type RightChatPanelProps = Omit<
  ChatPaneProps,
  | 'hostedThreadId'
  | 'hostedLinkedConversationId'
  | 'hostedThreadResolving'
  | 'hostedThreadTitle'
  | 'surfaceVariant'
> & {
  /** Expand / show agent panel when a thread is hosted here. */
  onAgentPanelHost?: () => void
}

/** Right-rail chat: binds registry.agentPanelThreadId to ChatPane when set. */
export function RightChatPanel({ onAgentPanelHost, ...chatProps }: RightChatPanelProps) {
  const registry = useChatSurfaceRegistry()
  const { activeWorkspaceIds } = useWorkspace()
  const agentThreadId = registry.agentPanelThreadId
  const restoreAttemptedRef = useRef(false)
  /** Distinguishes remount-with-same-thread from a newly hosted thread. */
  const prevHostedThreadRef = useRef<string | null | undefined>(undefined)

  const { linkedConversationId, resolvedTitle, resolving } = useHostedChatThread(agentThreadId ?? undefined, {
    onTitleResolved: registry.registerThreadTitle,
  })

  useEffect(() => {
    const prev = prevHostedThreadRef.current
    prevHostedThreadRef.current = agentThreadId ?? null

    if (!agentThreadId) return

    // Collapsed rail remount (flex sibling swap) must not re-expand via the same hosted thread.
    // Still expand when a *new* thread is hosted while the strip is collapsed.
    if (chatProps.collapsed && (prev === undefined || prev === agentThreadId)) {
      return
    }

    onAgentPanelHost?.()
  }, [agentThreadId, onAgentPanelHost, chatProps.collapsed])

  /**
   * After idle / empty welcome (no hosted thread), restore the last agent-panel thread.
   * Do NOT restore when the panel hosts any thread (real or `new-*`) - restoring on
   * every transition to `new-*` immediately undid the + button, and focus-restore of
   * blanks stole the panel to a stale last-thread (Weekly Todo Triage).
   */
  useEffect(() => {
    if (chatProps.collapsed) {
      restoreAttemptedRef.current = false
      return
    }
    if (agentThreadId) {
      restoreAttemptedRef.current = false
      return
    }
    if (restoreAttemptedRef.current) return
    restoreAttemptedRef.current = true
    void registry.restoreLastAgentPanelThread('mount-empty', { activeWorkspaceIds })
  }, [agentThreadId, chatProps.collapsed, registry, activeWorkspaceIds])

  const handlePanelFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (chatProps.collapsed) return
    // Only when the panel hosts nothing. Never on focus while a real chat or `new-*`
    // is bound - that path switched users to Weekly Todo Triage mid-reply.
    // Also skip Ask/Plan/Agent and a composer that already has unsent text (2026-09-01).
    if (!shouldRestoreAgentPanelOnComposerFocus(agentThreadId, event.target)) return
    void registry.restoreLastAgentPanelThread('focus-empty', { activeWorkspaceIds })
  }, [agentThreadId, chatProps.collapsed, registry, activeWorkspaceIds])

  /** Agent panel rail; always compact even before a thread is bound. */
  return (
    <div
      className="flex h-full min-h-0 flex-col"
      onFocusCapture={handlePanelFocus}
    >
      <ChatPane
        {...chatProps}
        hostedThreadId={agentThreadId ?? undefined}
        hostedThreadTitle={resolvedTitle}
        hostedLinkedConversationId={agentThreadId ? linkedConversationId : undefined}
        hostedThreadResolving={agentThreadId ? resolving : false}
        surfaceVariant="compact"
      />
    </div>
  )
}
